/**
 * React Query hooks for Products data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  searchOpenFoodFacts,
} from '@/lib/api/products';
import type {
  SearchProductsQuery,
  CreateProductRequest,
  UpdateProductRequest,
} from '@/types/api';
import { productKeys } from '@/lib/query-keys';

export { productKeys };

/**
 * Fetch saved products
 */
export function useProducts(query: SearchProductsQuery = {}) {
  return useQuery({
    queryKey: productKeys.list(query),
    queryFn: () => getProducts(query),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single product
 */
export function useProduct(id: number) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => getProductById(id),
    enabled: Number.isFinite(id) && id > 0,
  });
}

/**
 * Search OpenFoodFacts (only runs when query is provided)
 */
export function useOffSearch(query: string, enabled: boolean) {
  return useQuery({
    queryKey: productKeys.offSearch(query),
    queryFn: () => searchOpenFoodFacts(query),
    enabled: enabled && query.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProductRequest) => createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProductRequest }) =>
      updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
